import { sortCurrencies } from './webledgerCurrencyOrder.js';
import type { PresenceUser } from '@colplat/protocol';
import { fromScaled, toScaled } from '@colplat/ui-web';

/**
 * The arithmetic and grouping behind webledger's three reports, kept apart
 * from the rendering so it can be tested without a DOM.
 *
 * Everything here sums money with scaled `BigInt`, never `Number`. Legacy did
 * this with floats and then hid the consequences behind epsilons
 * (`Math.abs(totals[c]) > 1e-7` when deciding which currency chips to draw,
 * `ABS(SUM(amount)) > 0.0001` in the trial balance's SQL). Those epsilons are
 * not carried forward, because with exact arithmetic there is nothing for
 * them to hide — and each one was also quietly suppressing genuine tiny
 * balances, which in a ledger is the wrong way round: a balance of 0.0001 is
 * small, not absent.
 */

// ---------------------------------------------------------------- shapes

export interface ReportSplitDto {
  id: string;
  accountId: string;
  memo: string;
  version: number;
  value: string;
  amount: string;
  currencyCode: string;
  accountName: string;
  accountType: string;
  accountPath: string;
  accountLabel: string;
  inScope: boolean;
}

export interface ReportTransactionDto {
  id: string;
  txDate: string;
  description: string;
  reference: string;
  currencyCode: string;
  seq: number | null;
  version: number;
  /** As the register's rows carry them — see webledgerRecordState.ts. */
  lockedBy?: PresenceUser | null;
  ownedBy?: PresenceUser | null;
  ownedSince?: string | null;
  splits: ReportSplitDto[];
}

export interface TrialBalanceRowDto {
  accountId: string; name: string; type: string; currencyCode: string; balance: string;
}

export interface GeneralLedgerRowDto {
  accountId: string; accountName: string; currencyCode: string;
  txDate: string; description: string; reference: string; memo: string; amount: string;
}

// ---------------------------------------------------------------- running balances

export interface RunningBalances {
  /** Per transaction id: the per-currency running balance *after* that transaction. */
  after: Map<string, Record<string, string>>;
  /** The final position per currency — the same numbers the top row shows. */
  totals: Record<string, string>;
}

/**
 * The per-currency running balance, accumulated chronologically.
 *
 * This is the report's least obvious number and the one most often
 * misunderstood, so, stated plainly: it is the **net balance of the counted
 * legs**, not a sum of transaction sizes. Money leaving a currency reduces
 * that currency's running — the CAD leg of a CAD→HUF exchange takes CAD down
 * and brings HUF up — so the final figure is the current net position per
 * currency rather than a turnover total.
 *
 * Rows arrive newest-first, so the accumulation runs over a reversed copy and
 * the **top** row ends up showing the totals. Legs marked out of scope by an
 * active purpose or folder filter contribute nothing, which is what makes a
 * folder view total the folder's own movement instead of the counter-legs
 * elsewhere in the chart of accounts.
 */
export function runningBalances(rows: readonly ReportTransactionDto[]): RunningBalances {
  const running = new Map<string, bigint>();
  const after = new Map<string, Record<string, string>>();

  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const tx = rows[i]!;
    const touched = new Set<string>();
    for (const split of tx.splits) {
      if (!split.inScope) continue;
      const code = split.currencyCode;
      running.set(code, (running.get(code) ?? 0n) + toScaled(split.amount));
      touched.add(code);
    }
    // Only the currencies this transaction moved, matching legacy: a row's
    // Running column speaks about the legs on that row, and printing every
    // currency the report has ever seen against a single-currency
    // transaction would be noise.
    const snapshot: Record<string, string> = {};
    for (const code of touched) snapshot[code] = fromScaled(running.get(code)!);
    after.set(tx.id, snapshot);
  }

  const totals: Record<string, string> = {};
  for (const [code, value] of running) totals[code] = fromScaled(value);
  return { after, totals };
}

/** The currencies worth drawing a chip for: a balance that is exactly zero is not a position. */
/** The currencies with something in them, in the Settings page's order (`sortCurrencies`). */
export function nonZeroCurrencies(totals: Record<string, string>): string[] {
  return sortCurrencies(Object.keys(totals).filter((code) => toScaled(totals[code]!) !== 0n));
}

// ---------------------------------------------------------------- grouping

export interface DateGroup { date: string; transactions: ReportTransactionDto[] }

/**
 * Consecutive runs of the same date, not a keyed bucket — the rows are
 * already ordered by date, and grouping by key would silently repair an
 * ordering bug rather than show it.
 */
export function groupByDate(rows: readonly ReportTransactionDto[]): DateGroup[] {
  const groups: DateGroup[] = [];
  for (const tx of rows) {
    const last = groups[groups.length - 1];
    if (last && last.date === tx.txDate) last.transactions.push(tx);
    else groups.push({ date: tx.txDate, transactions: [tx] });
  }
  return groups;
}

export interface TrialBalanceGroup {
  currencyCode: string;
  rows: TrialBalanceRowDto[];
  /** Sum of the positive balances, as decimal text. */
  totalDebit: string;
  /** Sum of the negative balances, as a positive magnitude. */
  totalCredit: string;
}

/**
 * One card per currency, with the debit and credit columns totalled.
 *
 * A trial balance that balances has equal totals; one that does not is
 * evidence of a real problem, so the two are summed independently and shown
 * as they come out rather than being derived from one another.
 */
export function trialBalanceGroups(rows: readonly TrialBalanceRowDto[]): TrialBalanceGroup[] {
  const byCurrency = new Map<string, TrialBalanceRowDto[]>();
  for (const row of rows) {
    const list = byCurrency.get(row.currencyCode) ?? [];
    list.push(row);
    byCurrency.set(row.currencyCode, list);
  }
  // One section per currency, in the Settings page's order.
  return sortCurrencies(byCurrency.keys()).map((currencyCode) => {
    const group = byCurrency.get(currencyCode)!;
    let debit = 0n;
    let credit = 0n;
    for (const row of group) {
      const balance = toScaled(row.balance);
      if (balance > 0n) debit += balance;
      else credit += -balance;
    }
    return { currencyCode, rows: group, totalDebit: fromScaled(debit), totalCredit: fromScaled(credit) };
  });
}

export interface GeneralLedgerGroup {
  accountId: string;
  accountName: string;
  currencyCode: string;
  rows: GeneralLedgerRowDto[];
  /** Net movement over the period — the footer figure. */
  net: string;
}

/**
 * Section breaks on (account, currency), in the order the server sent.
 *
 * Consecutive-run grouping again, for the same reason as `groupByDate`, and
 * with more at stake: the server's `ORDER BY` is what decides these sections,
 * so if it ever changes, this should visibly fall apart rather than quietly
 * reassemble the rows into sections the ordering no longer supports.
 */
export function generalLedgerGroups(rows: readonly GeneralLedgerRowDto[]): GeneralLedgerGroup[] {
  const groups: GeneralLedgerGroup[] = [];
  let net = 0n;
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.accountId === row.accountId && last.currencyCode === row.currencyCode) {
      last.rows.push(row);
    } else {
      if (last) last.net = fromScaled(net);
      net = 0n;
      groups.push({
        accountId: row.accountId, accountName: row.accountName, currencyCode: row.currencyCode,
        rows: [row], net: '0.0000',
      });
    }
    net += toScaled(row.amount);
  }
  const last = groups[groups.length - 1];
  if (last) last.net = fromScaled(net);
  return groups;
}


// ---------------------------------------------------------------- converted totals

export interface FxRates {
  base: string;
  date: string | null;
  rates: Record<string, number>;
  stale: boolean;
  error: string | null;
  totalsCurrencies: Array<{ code: string; name: string }>;
}

/**
 * Cross-rate through the base: `(amount / rates[from]) * rates[to]`.
 *
 * Returns null when either side has no rate, and every caller renders nothing
 * rather than a wrong number — a converted total quietly missing one of its
 * currencies is worse than one that admits it cannot be computed.
 *
 * Floats here, deliberately, where everything else in this module uses exact
 * arithmetic. A rate *is* a float, and a converted total is a convenience for
 * reading: legacy's SPEC is explicit that it is never stored and never used
 * to compute a `value` or an `amount`. The exactness that matters is in the
 * figures being converted, which are exact before this touches them.
 */
export function convert(
  amount: number, from: string, to: string, rates: Record<string, number>,
): number | null {
  if (from === to) return amount;
  const rf = rates[from];
  const rt = rates[to];
  if (!rf || !rt) return null;
  return (amount / rf) * rt;
}

export interface ConvertedTotal {
  amount: number;
  /** Currencies that had no rate and so are missing from `amount`. */
  missing: string[];
}

/** Every per-currency total added up in one currency, saying what it could not convert. */
export function convertTotals(
  totals: Record<string, string>, target: string, rates: Record<string, number>,
): ConvertedTotal {
  let amount = 0;
  const missing: string[] = [];
  for (const code of nonZeroCurrencies(totals)) {
    const converted = convert(Number(totals[code]!), code, target, rates);
    if (converted === null) missing.push(code);
    else amount += converted;
  }
  return { amount, missing };
}
